import { FileValidation } from './types';

export const MAX_FILE_BYTES = 25 * 1024 * 1024;

const ALLOWED = new Set<string>([
  'application/pdf',
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain', 'text/csv',
]);

export const isAllowedMime = (mime: string): boolean => ALLOWED.has(mime);

export const validateFile = (file: { type: string; size: number }): FileValidation => {
  if (!isAllowedMime(file.type)) return { ok: false, error: 'file type not allowed' };
  if (file.size > MAX_FILE_BYTES) return { ok: false, error: 'file exceeds 25 MB' };
  return { ok: true };
};

const slug = (s: string): string => s.trim().replace(/\s+/g, '-');

export const buildStoragePath = (accountPrefix: string, clientId: string, ts: number, filename: string): string =>
  `${accountPrefix}documents/${clientId}/${ts}-${slug(filename)}`;

export const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
};

export const isImage = (mime: string): boolean => mime.startsWith('image/');

export const buildFolderPath = (
  folderId: string | null,
  foldersById: { [id: string]: { name: string; parentId: string | null } }
): string => {
  const parts: string[] = [];
  const seen = new Set<string>();
  let cur = folderId;
  while (cur && foldersById[cur] && !seen.has(cur)) {
    seen.add(cur);
    parts.unshift(foldersById[cur].name);
    cur = foldersById[cur].parentId || null;
  }
  return parts.join(' / ');
};

export const isPdf = (mime: string): boolean => mime === 'application/pdf';

/** Query parameter that opens one document in the client's private area (/dashboard/my-documents?doc=<id>). */
export const DOCUMENT_DEEP_LINK_PARAM = 'doc';

/**
 * Link to the client's private area, or to one document in it. `origin` is the site root without a trailing slash
 * (https://menorcahomecare.com). Reused by the e-mail callable's twin on the server and by anything that sends the
 * link by other channels (WhatsApp).
 */
export const buildDocumentDeepLink = (origin: string, documentId?: string | null): string => {
  const base = `${origin.replace(/\/+$/, '')}/dashboard/my-documents`;
  return documentId ? `${base}?${DOCUMENT_DEEP_LINK_PARAM}=${encodeURIComponent(documentId)}` : base;
};

/** The document id in a location.search string, or null. */
export const getDocumentIdFromSearch = (search: string): string | null => {
  const id = new URLSearchParams(search || '').get(DOCUMENT_DEEP_LINK_PARAM);
  return id && id.trim() ? id.trim() : null;
};

/** Ids of the folders from the document's folder up to the root (outermost last), so the tree can expand them. */
export const folderAncestorIds = (
  folderId: string | null,
  foldersById: { [id: string]: { name: string; parentId: string | null } }
): string[] => {
  const ids: string[] = [];
  const seen = new Set<string>();
  let cur = folderId;
  while (cur && foldersById[cur] && !seen.has(cur)) {
    seen.add(cur);
    ids.push(cur);
    cur = foldersById[cur].parentId || null;
  }
  return ids;
};
